import ErrorBox from "@/components/ErrorBox";
import Input from "@/components/Input";
import {ModalDialog} from "@/components/ModalDialog";
import RippleButton from "@/components/RippleButton";
import {auth} from "@/features/api";
import useSignOut from "@/features/auth/hooks/useSignOut";
import {getErrorText} from "@/utils/form";
import {useForm} from "@tanstack/react-form";
import {useEffect, useState, useRef} from "react";
import z from "zod";
import usePutUserVerifiedAge from "../../hooks/usePutUserVerifiedAge";
import {useSetLocalStorageMutation} from "@/hooks/useSetLocalStorageMutation";
import {useLocalStorageQuery} from "@/hooks/useLocalStorageQuery";
import usePostUserLeaveDelete from "../../hooks/usePostUserLeaveDelete";
import {getUserClaims} from "@/features/api/lib/firebase";
import {User} from "firebase/auth";

const VerifyUserAge = ({user}: {user: User | null | undefined}) => {
  const localStorageIsVerifiedAge = useLocalStorageQuery("isVerifiedAge");
  const localStorageIsYoungerThan15 = useLocalStorageQuery("isYoungerThan15");

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isVerifiedAge, setIsVerifiedAge] = useState<boolean | null>(null);
  const [displayExitMessage, setDisplayExitMessage] = useState(false);
  const [needMonth, setNeedMonth] = useState(false);
  const [needDay, setNeedDay] = useState(false);
  const [displayEnterMessage, setDisplayEnterMessage] = useState(false);
  const signOut = useSignOut(auth);
  const postUserLeaveDelete = usePostUserLeaveDelete();
  const putUserVerifiedAge = usePutUserVerifiedAge();
  const setLocalStorageMutation = useSetLocalStorageMutation();
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1; // Months are 0-indexed in JavaScript
  const currentDay = new Date().getDate();

  const error =
    setLocalStorageMutation.error ||
    postUserLeaveDelete.error ||
    putUserVerifiedAge.error ||
    localStorageIsVerifiedAge.error ||
    localStorageIsYoungerThan15.error;

  const isError =
    setLocalStorageMutation.isError ||
    postUserLeaveDelete.isError ||
    putUserVerifiedAge.isError ||
    localStorageIsVerifiedAge.isError ||
    localStorageIsYoungerThan15.isError;

  useEffect(() => {
    let isMounted = true;

    // if user is logged in
    if (user) {
      getUserClaims().then((userClaims) => {
        // if the component is still mounted, set the isVerifiedAge state
        // to the value from user claims or false if not present
        if (isMounted) {
          // get isVerifiedAge from user claims of Firebase token
          setIsVerifiedAge(userClaims?.isVerifiedAge ?? false);
        }
      });
    } else {
      // When user logs out or is not present, reset the verification state.
      setIsVerifiedAge(null);
    }

    return () => {
      isMounted = false;
    };
  }, [user]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    // Don't show the dialog while loading from local storage or user claims.
    if (localStorageIsVerifiedAge.data === undefined || isVerifiedAge === null)
      return;

    // If the user has not verified their age in local storage or through their profile, show the dialog.
    setIsDialogOpen(!localStorageIsVerifiedAge.data && !isVerifiedAge);
  }, [localStorageIsVerifiedAge.data, isVerifiedAge]);

  useEffect(() => {
    // If the user is younger than 15, show the exit message dialog after reloading the page.
    if (localStorageIsYoungerThan15.data) {
      setDisplayExitMessage(true);
    }
  }, [localStorageIsYoungerThan15.data]);

  const exit = () => {
    postUserLeaveDelete.mutate({});
    setDisplayExitMessage(true);

    // Set local storage to indicate the user is younger than 15
    // so they won't be asked again after reloading the page.
    setLocalStorageMutation.mutate({
      key: "isYoungerThan15",
      value: true,
    });
  };

  const leave = () => {
    postUserLeaveDelete.mutate(
      {},
      {
        // on settled (after success or error), sign the user out
        onSettled: (_, error) => {
          if (error) {
            // Wait 3 seconds before signing out to allow the user to see the error message.
            timeoutRef.current = setTimeout(() => {
              signOut.mutate();
            }, 3000);
          } else {
            signOut.mutate();
          }
        },
      },
    );
  };

  const enter = () => {
    putUserVerifiedAge.mutate({});
    setDisplayEnterMessage(true);

    // Set local storage to indicate the user has verified their age
    // so they won't be asked again after reloading the page.
    setLocalStorageMutation.mutate({
      key: "isVerifiedAge",
      value: true,
    });
  };

  const handleNextButton = (event: {
    value: {year: string; month: string; day: string};
  }) => {
    const year = parseInt(event.value.year, 10);
    const month = parseInt(event.value.month, 10);
    const day = parseInt(event.value.day, 10);

    // Calculate user's age and determine if they're 15 or older
    const userAge = currentYear - year;
    const isExactly15 = userAge === 15;
    const isOver15 = userAge > 15;

    if (isOver15) {
      enter();
      return;
    }

    if (!isExactly15) {
      exit();
      return;
    }

    // User is exactly 15 - need to check month
    setNeedMonth(true);
    if (!month) return;

    const monthDifference = currentMonth - month;
    const isBirthMonthPassed = monthDifference > 0;
    const isBirthMonthCurrent = monthDifference === 0;

    if (isBirthMonthPassed) {
      enter();
      return;
    }

    if (!isBirthMonthCurrent) {
      exit();
      return;
    }

    // Same birth month - need to check day
    setNeedDay(true);
    if (!day) return;

    const hasReachedBirthday = currentDay >= day;
    if (hasReachedBirthday) {
      enter();
    } else {
      exit();
    }
  };

  const form = useForm({
    defaultValues: {
      year: "",
      month: "",
      day: "",
    },
    onSubmit: handleNextButton,
  });

  const renderEnterMessage = () => {
    return (
      <div className="gap-g-3 flex flex-col items-center justify-center">
        <p>Děkujeme, vítejte v Trendaru!</p>
        <RippleButton
          variant="primary"
          disabled={putUserVerifiedAge.isError}
          isPending={putUserVerifiedAge.isPending}
          onClick={() => {
            setIsDialogOpen(false);
          }}
        >
          Vstoupit
        </RippleButton>
      </div>
    );
  };

  const renderExitMessage = () => {
    return (
      <div className="gap-g-3 flex flex-col items-center justify-center">
        <p className="text-center">
          Ajaj! Mrzí nás to, <br /> ale zatím nemůžeme zaměstnávat Trendaristy
          mladší 15 let.
        </p>
        <RippleButton
          variant="primary"
          disabled={postUserLeaveDelete.isError}
          isPending={postUserLeaveDelete.isPending}
          onClick={() => {
            setIsDialogOpen(false);
            signOut.mutate();
          }}
        >
          Vrátím se, až budu starší a (ne nutně) moudřejší.
        </RippleButton>
      </div>
    );
  };

  const renderForm = () => {
    return (
      <>
        <div className="text-center">
          <div className="mb-g-1 text-xl">Vítejte!</div>
          Než vstoupíte do Trendara, napište nám rok svého narození.
        </div>

        <form
          className="gap-g-2 flex w-full max-w-[320px] flex-col items-center self-center"
          spellCheck="false"
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            form.handleSubmit();
          }}
        >
          <form.Field
            name="year"
            validators={{
              onChange: z
                .string()
                .trim()
                .transform((val) => parseInt(val, 10))
                .refine(
                  (val) => val >= 1900 && val <= currentYear,
                  `Rok narození musí být mezi 1900 a ${currentYear}.`,
                ),
            }}
            children={(field) => {
              return (
                <Input
                  label="Rok narození"
                  type="number"
                  autoFocus
                  errorText={getErrorText(field)}
                  id={field.name}
                  name={field.name}
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.value)}
                />
              );
            }}
          />

          {needMonth && (
            <form.Field
              name="month"
              validators={{
                onChange: z
                  .string()
                  .trim()
                  .transform((val) => parseInt(val, 10))
                  .refine(
                    (val) => val >= 1 && val <= 12,
                    "Měsíc musí být mezi 1 a 12.",
                  ),
              }}
              children={(field) => {
                return (
                  <Input
                    label="No dobře, ještě měsíc (číslem)"
                    type="number"
                    autoFocus
                    errorText={getErrorText(field)}
                    id={field.name}
                    name={field.name}
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                  />
                );
              }}
            />
          )}

          {needDay && (
            <form.Field
              name="day"
              validators={{
                onChange: z
                  .string()
                  .trim()
                  .transform((val) => parseInt(val, 10))
                  .refine(
                    (val) => val >= 1 && val <= 31,
                    "Den musí být mezi 1 a 31.",
                  ),
              }}
              children={(field) => {
                return (
                  <Input
                    label="No tak teda ještě den"
                    type="number"
                    autoFocus
                    errorText={getErrorText(field)}
                    id={field.name}
                    name={field.name}
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                  />
                );
              }}
            />
          )}

          <form.Subscribe
            selector={(state) => ({
              canSubmit: state.canSubmit,
              isSubmitting: state.isSubmitting,
              values: state.values,
            })}
            children={({canSubmit, isSubmitting, values}) => (
              <RippleButton
                className="w-[240px] text-center"
                type="submit"
                variant="primary"
                centerText={true}
                disabled={
                  !canSubmit ||
                  !values.year ||
                  (needMonth && !values.month) ||
                  (needDay && !values.day)
                }
                isPending={isSubmitting}
              >
                <span>Pokračovat</span>
              </RippleButton>
            )}
          />
        </form>

        <div className="text-on-surface/70 mt-g-2 text-center select-none">
          Něco nesedí?{" "}
          <RippleButton
            variant="text"
            isPending={postUserLeaveDelete.isPending}
            onClick={leave}
          >
            Odhlásit se můžete tady.
          </RippleButton>
        </div>
      </>
    );
  };

  return (
    <ModalDialog
      header="Nový účet v Trendaru právě vzniká"
      hasCloseBtn={false}
      isOpen={isDialogOpen}
    >
      <div className="gap-g-3 flex flex-col justify-center">
        {displayEnterMessage && renderEnterMessage()}
        {displayExitMessage && renderExitMessage()}
        {!displayEnterMessage && !displayExitMessage && renderForm()}
        {isError && <ErrorBox error={error} />}
      </div>
    </ModalDialog>
  );
};

export default VerifyUserAge;
